/**
 * AG-UI Debug Logging
 *
 * Shared helpers for debug-gated console logging with human-readable
 * timestamps, used across the chat core, renderer, and state manager so
 * SSE event timing can be correlated during debugging.
 */

/**
 * Formats the current time as a human-readable timestamp with millisecond
 * precision, e.g. "14:32:05.123".
 */
export function formatDebugTimestamp(): string {
  const now = new Date();
  return `${now.toLocaleTimeString()}.${String(now.getMilliseconds()).padStart(3, '0')}`;
}

/**
 * A console logger that no-ops unless debug mode is enabled.
 */
export interface DebugLogger {
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Creates a debug logger that prefixes messages with a human-readable
 * timestamp. `isEnabled` is checked on every call so callers can back it
 * with a live config value or a fixed snapshot, as needed.
 */
export function createDebugLogger(isEnabled: () => boolean): DebugLogger {
  const withTimestamp = (args: unknown[]) => [`[${formatDebugTimestamp()}]`, ...args];
  return {
    log: (...args) => { if (isEnabled()) console.log(...withTimestamp(args)); },
    warn: (...args) => { if (isEnabled()) console.warn(...withTimestamp(args)); },
    error: (...args) => { if (isEnabled()) console.error(...withTimestamp(args)); },
  };
}
